import { useState, useEffect, useCallback } from 'react'
import { api } from '../../services/api'
import { PageHeader, StatusBadge, card, colors, btnSecondary } from '../../components/ui'

interface SyncStatus {
  readonly deviceId: string
  readonly queueDepth: number
  readonly lastSyncAt: string | null
  readonly lastSyncSuccess: boolean
  readonly lastSyncError: string | null
  readonly totalSynced: number
  readonly totalFailed: number
  readonly isOnline: boolean
  readonly uptimeSeconds: number
}

interface SystemStatus {
  readonly deployTarget: string
  readonly timestamp: string
  readonly checks: {
    readonly database?: { readonly status: string; readonly sizeBytes?: number; readonly serverTime?: string }
    readonly activity?: { readonly resultsLast24h?: number; readonly ordersLast24h?: number }
    readonly process?: {
      readonly uptimeSeconds: number
      readonly memoryMb: number
      readonly heapUsedMb: number
      readonly nodeVersion: string
    }
    readonly sync?: SyncStatus | null
  }
}

function formatUptime(seconds: number): string {
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  const mins = Math.floor((seconds % 3600) / 60)
  if (days > 0) return `${days}d ${hours}h ${mins}m`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

function formatBytes(bytes: number): string {
  if (bytes > 1073741824) return `${(bytes / 1073741824).toFixed(1)} GB`
  if (bytes > 1048576) return `${(bytes / 1048576).toFixed(1)} MB`
  return `${(bytes / 1024).toFixed(0)} KB`
}

function formatRelativeTime(iso: string | null): string {
  if (!iso) return 'Never'
  const diffMs = Date.now() - new Date(iso).getTime()
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 60) return `${Math.max(seconds, 0)}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 48) return `${hours}h ago`
  return `${Math.floor(hours / 24)}d ago`
}

export function SystemStatusPage() {
  const [status, setStatus] = useState<SystemStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchStatus = useCallback(async () => {
    try {
      const result = await api.get<SystemStatus>('/system/status')
      if (result.data) setStatus(result.data)
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load status')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchStatus()
    const interval = setInterval(fetchStatus, 15000)
    return () => clearInterval(interval)
  }, [fetchStatus])

  if (loading) return <div style={{ color: colors.textMuted }}>Loading system status...</div>

  return (
    <div>
      <PageHeader
        title="System Status"
        subtitle={status ? `${status.deployTarget} deployment` : ''}
        actions={
          <button onClick={fetchStatus} style={btnSecondary}>
            Refresh
          </button>
        }
      />

      {error && (
        <div
          style={{
            padding: '0.75rem',
            background: colors.bgError,
            border: `1px solid ${colors.borderError}`,
            borderRadius: '6px',
            color: colors.danger,
            marginBottom: '1rem',
          }}
        >
          {error}
        </div>
      )}

      {status && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
          <div style={card}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: colors.text }}>Database</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textMuted }}>Status</span>
                <StatusBadge
                  status={status.checks.database?.status ?? 'unavailable'}
                  label={status.checks.database?.status === 'ok' ? 'Connected' : 'Unavailable'}
                />
              </div>
              {status.checks.database?.sizeBytes && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textMuted }}>Size</span>
                  <span>{formatBytes(Number(status.checks.database.sizeBytes))}</span>
                </div>
              )}
            </div>
          </div>

          <div style={card}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: colors.text }}>Activity (24h)</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textMuted }}>Results</span>
                <span style={{ fontWeight: 600 }}>{status.checks.activity?.resultsLast24h ?? '--'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textMuted }}>Orders</span>
                <span style={{ fontWeight: 600 }}>{status.checks.activity?.ordersLast24h ?? '--'}</span>
              </div>
            </div>
          </div>

          <div style={card}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: colors.text }}>Process</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textMuted }}>Uptime</span>
                <span>{status.checks.process ? formatUptime(status.checks.process.uptimeSeconds) : '--'}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textMuted }}>Memory</span>
                <span>{status.checks.process?.memoryMb ?? '--'} MB</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: colors.textMuted }}>Node</span>
                <span>{status.checks.process?.nodeVersion ?? '--'}</span>
              </div>
            </div>
          </div>

          <div style={card}>
            <h3 style={{ margin: '0 0 1rem', fontSize: '1rem', color: colors.text }}>Cloud Sync</h3>
            {status.checks.sync ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textMuted }}>Status</span>
                  <StatusBadge status={status.checks.sync.isOnline ? 'online' : 'offline'} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textMuted }}>Queue</span>
                  <span style={{ fontWeight: 600 }}>{status.checks.sync.queueDepth}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textMuted }}>Last sync</span>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    {formatRelativeTime(status.checks.sync.lastSyncAt)}
                    {status.checks.sync.lastSyncAt && (
                      <StatusBadge
                        status={status.checks.sync.lastSyncSuccess ? 'active' : 'retired'}
                        label={status.checks.sync.lastSyncSuccess ? 'OK' : 'Fail'}
                      />
                    )}
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textMuted }}>Synced / Failed</span>
                  <span>
                    <span style={{ color: '#16a34a', fontWeight: 600 }}>{status.checks.sync.totalSynced}</span>
                    {' / '}
                    <span
                      style={{
                        color: status.checks.sync.totalFailed > 0 ? '#dc2626' : colors.textMuted,
                        fontWeight: 600,
                      }}
                    >
                      {status.checks.sync.totalFailed}
                    </span>
                  </span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: colors.textMuted }}>Agent uptime</span>
                  <span>{formatUptime(status.checks.sync.uptimeSeconds)}</span>
                </div>
                {status.checks.sync.lastSyncError && (
                  <div
                    style={{
                      marginTop: '0.25rem',
                      padding: '0.4rem 0.6rem',
                      background: colors.bgError,
                      borderRadius: '4px',
                      fontSize: '0.8rem',
                      color: colors.danger,
                    }}
                  >
                    {status.checks.sync.lastSyncError}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: colors.textMuted, fontSize: '0.875rem' }}>
                Sync agent is not running or has not reported status yet.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
